'use client';

import React from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/PulseUI';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faSliders } from '@fortawesome/pro-solid-svg-icons';
import { useAnalytics } from 'src/hooks/analytics';
import { selectActiveLocation } from 'src/redux/slices/analyticsSlice';
import { countAppliedFilterTypes } from 'src/components/FilterSets/SiteOfCare/displayHelpers';
import { PAYER_NAME_FILTER_LOCATIONS } from '../../constants';

const AnalyticsFilterButton = () => {
  const { closeAllButCurrent } = useDrawer();
  const { formattedFiltersForAPI, payerNamesFilterForAPI } = useAnalytics();
  const handleOpen = () => {
    closeAllButCurrent('analytics-filter-drawer');
  };

  // Payer names aren't part of the shared analytics filter payload, so count
  // them separately — and only on the insights that actually apply them, which
  // is the same gate the drawer uses to show the filter.
  const activeLocation = useSelector(selectActiveLocation);
  const countsPayerNames =
    PAYER_NAME_FILTER_LOCATIONS.includes(activeLocation) &&
    !!payerNamesFilterForAPI;

  // Not formattedFiltersForAPI.length: a single Site of Care selection emits a
  // SOC_TYPE and a SOC_SUBTYPE row, which counted as 2 filters.
  const count =
    countAppliedFilterTypes(formattedFiltersForAPI ?? []) +
    (countsPayerNames ? 1 : 0);

  return (
    <Button
      variant="outlined"
      color="neutral"
      className="!px-4 rounded-full text-sm font-normal !h-9 cursor-pointer"
      onClick={handleOpen}
      data-testid="analytics-filter-button"
    >
      <div className="flex items-center gap-1.5 justify-center">
        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
          <Icon
            icon={faSliders}
            className="w-4 h-4 text-secondary"
            data-testid="analytics-filter-icon"
          />
        </div>
        <span
          data-testid="analytics-filter-label"
          className="self-stretch justify-start text-primary text-sm font-medium font-['Inter'] leading-5 line-clamp-1"
        >
          Filters
        </span>
        <div
          className="bg-sky-100 flex justify-center items-center w-4 h-5 gap-1 rounded-full p-1"
          data-testid="analytics-filter-badge"
        >
          <div
            className="text-center text-neutral-700 text-xs font-bold leading-none"
            data-testid="analytics-filter-count"
          >
            {count}
          </div>
        </div>
      </div>
    </Button>
  );
};

export default AnalyticsFilterButton;
